import moment from "moment";
import {
  dateModifiedForTimesheet,
  getShiftLength,
  manuallyEmbedDateWithTime,
} from "Utils/utils";
import { formatDurationMinutes } from "../components/TimesheetRevamp/TimesheetTabPrimitives";

const normalizeClockHHMM = (time) => {
  if (!time) return "";
  const parsed = moment(time, ["HH:mm", "HH:mm:ss", "h:mm A"], true);
  if (parsed.isValid()) return parsed.format("HH:mm");
  const str = String(time);
  return str.length >= 5 ? str.slice(0, 5) : str;
};

/** True when billing start/end datetimes fall on different calendar days. */
export const shiftClocksSpanNextCalendarDay = (startDateTime, endDateTime) => {
  const start = parseShiftDateTime(startDateTime);
  const end = parseShiftDateTime(endDateTime);
  return Boolean(start?.isValid?.() && end?.isValid?.() && end.isAfter(start, "day"));
};

/**
 * Basic shift duration from clock times (HH:mm).
 * 12:00 AM → 12:00 AM next calendar day = 24:00 (getShiftLength alone yields 00:00).
 */
export const resolveBasicShiftDurationHHMM = (
  startClock,
  endClock,
  { spansNextCalendarDay = false } = {}
) => {
  if (!startClock || !endClock) return null;
  const start = normalizeClockHHMM(startClock);
  const end = normalizeClockHHMM(endClock);
  const shiftLength = getShiftLength(start, end, true);
  if (shiftLength === "00:00" && spansNextCalendarDay && start === end) {
    return "24:00";
  }
  return shiftLength;
};

const DATETIME_FORMATS = [
  "YYYY-MM-DD HH:mm:ss",
  "YYYY-MM-DD HH:mm",
  "YYYY-MM-DDTHH:mm:ss.SSS[Z]",
  "YYYY-MM-DDTHH:mm:ss",
  moment.ISO_8601,
];

export const parseShiftDateTime = (value) => {
  if (value == null || value === "") return null;
  const strict = moment(value, DATETIME_FORMATS, true);
  if (strict.isValid()) return strict;
  const loose = moment(value);
  return loose.isValid() ? loose : null;
};

export const resolveTimesheetDateLabel = (dateSource) => {
  if (!dateSource) return null;
  if (dateSource instanceof Date && !Number.isNaN(dateSource.getTime())) {
    return moment(dateSource).format("DD/MM/YYYY");
  }
  const label = dateModifiedForTimesheet(dateSource);
  return label || null;
};

export const buildShiftDateTime = (timeHHmm, dateSource) => {
  if (!timeHHmm || !dateSource) return null;
  const dateLabel = resolveTimesheetDateLabel(dateSource);
  if (!dateLabel) return null;
  const iso = manuallyEmbedDateWithTime(timeHHmm, dateLabel);
  return parseShiftDateTime(iso);
};

/** Org / timesheet setting: "scheduled" | "actual" (default actual). */
export const resolveTimesheetTimePreference = (shiftDetails, spSettings) => {
  const pref =
    shiftDetails?.time_display_preference ?? spSettings?.time_display_preference;
  return pref === "scheduled" ? "scheduled" : "actual";
};

/**
 * Basic-shift invoice time basis: "scheduled" | "actual" (default scheduled).
 * Advanced/split shifts always return "scheduled" (no Actual Time option).
 */
export const resolveInvoiceTimePreference = (
  shiftDetails,
  spSettings,
  { isSplitOrAdvance = false } = {}
) => {
  if (isSplitOrAdvance) {
    return "scheduled";
  }
  const pref =
    shiftDetails?.invoice_time_preference ??
    spSettings?.invoice_time_preference;
  return pref === "actual" ? "actual" : "scheduled";
};

export const getBillingShiftDateTimes = (shiftDetails, timeDisplayPreference) => {
  if (!shiftDetails) return { start: null, end: null };
  const useScheduled = timeDisplayPreference === "scheduled";
  const start = parseShiftDateTime(
    useScheduled ? shiftDetails.start_time : shiftDetails.worker_start_time
  );
  const end = parseShiftDateTime(
    useScheduled ? shiftDetails.end_time : shiftDetails.worker_end_time
  );
  return { start, end };
};

export const getShiftDurationMinutes = (shiftDetails, timeDisplayPreference) => {
  const { start, end } = getBillingShiftDateTimes(
    shiftDetails,
    timeDisplayPreference
  );
  return getDurationMinutesBetweenMoments(start, end);
};

export const getShiftDurationMinutesFromParts = ({
  startTime,
  endTime,
  startDateSource,
  endDateSource,
}) => {
  const start = buildShiftDateTime(startTime, startDateSource);
  const end = buildShiftDateTime(endTime, endDateSource);
  return getDurationMinutesBetweenMoments(start, end);
};

export const getDurationMinutesBetweenMoments = (start, end) => {
  if (!start?.isValid?.() || !end?.isValid?.()) return null;
  let endMoment = end.clone();
  if (endMoment.isBefore(start)) {
    endMoment = endMoment.add(1, "day");
  }
  // Match shift settings / breakdown validation: HH:mm clock diff, not
  // sub-minute truncation from stored datetimes (e.g. 16:17 vs 16:16).
  const startClock = start.format("HH:mm");
  const endClock = endMoment.format("HH:mm");
  const startAsClock = moment(startClock, "HH:mm");
  let endAsClock = moment(endClock, "HH:mm");
  if (endAsClock.isBefore(startAsClock)) {
    endAsClock = endAsClock.add(1, "day");
  }
  let minutes = endAsClock.diff(startAsClock, "minutes");
  // Midnight → midnight next day: equal clocks but full 24h window.
  if (minutes === 0 && startClock === endClock && endMoment.isAfter(start, "day")) {
    minutes = 24 * 60;
  }
  return minutes >= 0 ? minutes : null;
};

export const minutesToHHMM = (totalMinutes) => {
  if (totalMinutes == null || Number.isNaN(totalMinutes)) return null;
  const abs = Math.max(0, Math.round(totalMinutes));
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

/**
 * Billing insight range: start datetime – end datetime · Xh Ym
 * Duration and endpoints follow org time_display_preference (scheduled vs actual).
 */
export const formatCodeAllocationShiftRangeLabel = (
  shiftDetails,
  timeDisplayPreference
) => {
  const { start, end } = getBillingShiftDateTimes(
    shiftDetails,
    timeDisplayPreference
  );
  if (!start || !end) return null;

  const endMoment = end.isBefore(start) ? end.clone().add(1, "day") : end;
  const durationLabel = formatDurationMinutes(
    getDurationMinutesBetweenMoments(start, end)
  );
  const startLabel = start.format("DD MMM YYYY hh:mm A");
  const endLabel = endMoment.format("DD MMM YYYY hh:mm A");

  return `${startLabel} – ${endLabel}${
    durationLabel ? ` · ${durationLabel}` : ""
  }`;
};
